<template>
  <div class="w-100 d-flex flex-column animation-slide" v-if="!isLoading">
    <div
      @click="isCollapsed = !isCollapsed"
      class="player-uuid-info py-1"
      :class="[playerStatusClass, !isCollapsed ? 'collapsed-playeruuid' : '']"
    >
      <div class="uuid-value">
        <span>{{ playerInfoDetail.uuid }}</span>
      </div>
      <button class="history-btn" @click.stop="openHistory(playerInfoDetail)">
        HISTORY<i class="fa-solid ms-2 fa-clock-rotate-left"></i>
      </button>
      <div class="collapse-section">
        <i
          class="fa-solid fa-chevron-up"
          :class="!isCollapsed ? 'rotate-chevron' : ''"
        ></i>
      </div>
    </div>

    <div
      class="player-card p-0"
      :class="isCollapsed ? 'expanded' : ''"
      v-if="isCollapsed"
    >
      <div class="card-section general-info">
        <div class="section-header" :class="playerStatusClass">
          <span class="section-icon"><i class="fas fa-user-circle"></i></span>
          <h3 class="section-title">General Info</h3>
        </div>
        <div class="section-content">
          <div class="info-item" v-for="(value, key) in generalInfo" :key="key">
            <div class="item-icon" :class="getIconColorClass(key)">
              <i :class="getIconClass(key)"></i>
            </div>
            <div class="item-details">
              <div class="item-label">{{ formatLabel(key) }}</div>
              <div
                class="d-flex align-items-center justify-content-start w-100"
              >
                <div
                  class="item-value"
                  :title="value"
                  :class="{
                    highlight: isHighlightField(key),
                    'balance-value': key === 'balance',
                  }"
                  v-html="formatValue(key, value)"
                ></div>
                <div class="ms-2" v-if="key === 'user_id'">
                  <i
                    class="fa-solid fa-eye eye-btn"
                    @click="openInfo(value)"
                  ></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Game Info Section -->
      <div class="card-section game-info">
        <div class="section-header" :class="playerStatusClass">
          <span class="section-icon"><i class="fas fa-gamepad"></i></span>
          <h3 class="section-title">Game Info</h3>
        </div>
        <div class="section-content">
          <div class="info-item" v-for="(value, key) in gameInfo" :key="key">
            <div class="item-icon" :class="getIconColorClass(key)">
              <i :class="getIconClass(key)"></i>
            </div>
            <div class="item-details">
              <div class="item-label">{{ formatLabel(key) }}</div>
              <div
                class="item-value"
                :class="{
                  highlight: isHighlightField(key),
                  'win-value': key === 'win' && value > 0,
                  [`type-${playerInfoDetail?.type_id} type-of-bet type-id-custom`]:
                    key === 'type_id',
                }"
                v-html="formatValue(key, value)"
              ></div>
              <small
                class="campaign_id-val"
                v-if="key === 'freebet_id' && playerInfoDetail.campaign_id"
                >Campaign ID: {{ playerInfoDetail.campaign_id }}</small
              >
            </div>
          </div>
        </div>
      </div>

      <!-- Other Info Section -->
      <div class="card-section balance-info" v-if="0">
        <div class="section-header" :class="playerStatusClass">
          <span class="section-icon"><i class="fas fa-wallet"></i></span>
          <h3 class="section-title">Other Info</h3>
        </div>
        <div class="section-content">
          <div class="info-item" v-for="(value, key) in balanceInfo" :key="key">
            <div class="item-icon" :class="getIconColorClass(key)">
              <i :class="getIconClass(key)"></i>
            </div>
            <div class="item-details">
              <div class="item-label">{{ formatLabel(key) }}</div>
              <div
                class="item-value"
                :class="{
                  highlight: isHighlightField(key),
                  'balance-value': key === 'balance',
                }"
              >
                {{ formatValue(key, value) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="waiting-loader" v-else>
    <Loader />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Loader from "../Loader.vue";

export default {
  name: "PlayerCardDetail",
  props: {
    playerInfoDetail: {
      type: Object,
      required: true,
    },
    isLoading: {
      type: Boolean,
    },
    openPlayerDetail: Function,
  },
  components: {
    Loader,
  },
  data() {
    return {
      isCollapsed: window.innerWidth > 767,
      iconMap: {
        amount: { icon: "fas fa-coins", colorClass: "amount-icon" },
        win: { icon: "fas fa-trophy", colorClass: "win-icon" },
        latency: { icon: "fas fa-tachometer-alt", colorClass: "latency-icon" },
        balance: { icon: "fas fa-wallet", colorClass: "balance-icon" },
        currency_code: {
          icon: "fas fa-money-bill-wave",
          colorClass: "currency-icon",
        },
        created_at: { icon: "fas fa-calendar-alt", colorClass: "date-icon" },
        updated_at: { icon: "fas fa-calendar-check", colorClass: "date-icon" },
        user_id: { icon: "fas fa-id-card", colorClass: "user-icon" },
        roundId: { icon: "fas fa-dice", colorClass: "round-icon" },
        type_id: { icon: "fas fa-tag", colorClass: "type-icon" },
        cashedOut: {
          icon: "fas fa-hand-holding-usd",
          colorClass: "cashout-icon",
        },
        freebet_id: { icon: "fas fa-gift", colorClass: "freebet-icon" },
        autocashout: { icon: "fas fa-robot", colorClass: "autocashout-icon" },
        connection_id: {
          icon: "fas fa-link",
          colorClass: "connection-id-icon",
        },
        token: { icon: "fas fa-square-binary", colorClass: "token-icon" },
        refund_id: {
          icon: "fas fa-rotate-right",
          colorClass: "refund-id-icon",
        },
      },
    };
  },
  computed: {
    ...mapGetters(["allowedSkins", "isAdmin", "isMenuOpen", "chatSkinFilter" , "getTypeIDFromRow"]),

    // Return the player status class based on win/bet/refund
    playerStatusClass() {
      return this.checkIfWinBetOrRefund(this.playerInfoDetail);
    },

    generalInfo() {
      return {
        user_id: this.playerInfoDetail.user_id,
        token: this.playerInfoDetail.token,
        connection_id: this.playerInfoDetail.connection_id,
        latency: this.playerInfoDetail.latency,
        balance: this.playerInfoDetail.balance,
        currency_code: this.playerInfoDetail.currency_code,
        created_at: this.playerInfoDetail.created_at,
        updated_at: this.playerInfoDetail.updated_at,
      };
    },
    gameInfo() {
      return {
        amount: this.playerInfoDetail.amount_converted,
        win: this.playerInfoDetail.win_converted,
        is_valid: this.playerInfoDetail.is_valid,
        cashedOut: this.playerInfoDetail.cashedOut,
        autocashout: this.playerInfoDetail.autocashout,
        type_id: this.playerInfoDetail.type_id,
        refund_id: this.playerInfoDetail.refund_id,
        freebet_id: this.playerInfoDetail.freebet_id,
      };
    },
    // balanceInfo() {
    //   return {

    //   };
    // },
  },
  methods: {
    getIconClass(key) {
      return this.iconMap[key]?.icon || "fas fa-info-circle";
    },

    getIconColorClass(key) {
      return this.iconMap[key]?.colorClass || "default-icon";
    },
    openHistory(row) {
      let round_id = "";
      round_id = row.roundId + "_" + row.uuid;
      const token = row.token;
      let gameID = this.$store?.getters?.selectedDatabase || "theearcasino";
      let prefix = "https://stage-casino.theearplatform.com/";
      let link = `${prefix}moregames/historyPage/?round_id=${round_id}&game_id=${gameID}&token=${token}`;
      setTimeout(() => {
        window.open(link, "_blank");
      });
    },
    formatLabel(key) {
      const labels = {
        amount: "Bet",
        win: "Win",
        latency: "Latency",
        balance: "Balance",
        currency_code: "Currency",
        created_at: "Start Time",
        updated_at: "Cashout Time",
        user_id: "Player ID",
        roundId: "Round ID",
        type_id: "Type ID",
        cashedOut: "Cashed Out",
        refund_id: "Refund Reason",
        freebet_id: "Freebet ID",
      };
      return (
        labels[key] ||
        key
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ")
      );
    },

    formatValue(key, value) {
      const formatMap = {
        created_at: () => this.formatDateVal(value),
        updated_at: () => this.formatDateVal(value),
        amount: () =>
          `${parseFloat(this.playerInfoDetail.amount).toFixed(2)} ${
            this.playerInfoDetail.currency_code || ""
          } / ${parseFloat(this.playerInfoDetail.amount_converted).toFixed(
            2
          )} EUR`,
        win: () =>
          `${parseFloat(this.playerInfoDetail.win).toFixed(2)} ${
            this.playerInfoDetail.currency_code || ""
          } / ${this.formatCurrency(this.playerInfoDetail.win_converted)} EUR`,
        balance: () => parseFloat(value).toFixed(2),
        refund_id: () => this.getRefundStatus(value),
        autocashout: () =>
          parseInt(value) !== 0
            ? `x ${parseFloat(value).toFixed(2)}`
            : "Not setted",
        cashedOut: () =>
          parseFloat(value) > 0 ? `x ${parseFloat(value).toFixed(2)}` : "- -",
        latency: () => `${value} ms`,
        is_valid: () =>
          value
            ? '<i class="bi bi-patch-check-fill text-success"></i>'
            : '<i class="bi bi-slash-circle-fill text-danger"></i>',
        type_id: () => this.getTypeIDFromRow(this.playerInfoDetail),
      };

      return formatMap[key] ? formatMap[key]() : value || "- -";
    },

    getRefundStatus(refund_id) {
      switch (refund_id) {
        case 0:
          return "Not refunded";
        case 1:
          return "Refund by player";
        case 2:
          return "Start Round";
        case 3:
          return "Timeout Request (>6s)";
        case 4:
          return "";
        case 5:
          return "Win delayed / Disconnected";
        default:
          return "";
      }
    },

    // getTypeIDFromRow(row) {
    //   let typeID = row.type_id;
    //   return typeID == 1 || typeID == 2
    //     ? `Cashout ${typeID}`
    //     : typeID == 3
    //     ? row.value
    //     : "Ranges" +
    //       " " +
    //       (row.value == "0"
    //         ? "(1.00-1.40)"
    //         : row.value == "1"
    //         ? "(1.41-2.80)"
    //         : "(2.81+)");
    // },

    formatDateVal(dateString, onlyYMD = false) {
      if (!dateString) return "-";

      const date = new Date(dateString);

      if (onlyYMD) {
        const yyyy = date.getUTCFullYear();
        const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
        const dd = String(date.getUTCDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
      }

      const options = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZone: "UTC",
        hour12: false,
      };

      return date.toLocaleString("it-IT", options);
    },

    formatCurrency(value) {
      if (value === null || value === undefined) return "N/A";
      return value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    isHighlightField(key) {
      const highlightFields = ["balance", "win", "amount", "user_id"];
      return highlightFields.includes(key);
    },

    checkIfWinBetOrRefund(player) {
      if (player.win > 0) return "win";
      if (player.refund_id === 0) return "bet";
      if (player.refund_id > 0) return "refund";
      return "";
    },
    openInfo(val) {
      let row = {
        user_id: val,
      };
      this.openPlayerDetail(row);
    },
  },
};
</script>

<style lang="scss" scoped>
$win: #326c4d;
$bet: #1e3a8a;
$refund: #bc3144;

.win {
  background: $win;
}

.bet {
  background: $bet;
}

.refund {
  background: $refund;
}

.player-card {
  display: flex;
  flex-direction: column;
  gap: 0rem;
  padding: 1rem;
  width: 100%;
  zoom: 85%;
  // user-select: none;
}

.player-card.expanded {
  -webkit-animation: scale-in-ver-top 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)
    both;
  animation: scale-in-ver-top 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;

  /* ----------------------------------------------
 * Generated by Animista on 2025-6-4 9:28:15
 * Licensed under FreeBSD License.
 * See http://animista.net/license for more info. 
 * w: http://animista.net, t: @cssanimista
 * ---------------------------------------------- */

  /**
 * ----------------------------------------
 * animation scale-in-ver-top
 * ----------------------------------------
 */
  @-webkit-keyframes scale-in-ver-top {
    0% {
      -webkit-transform: scaleY(0);
      transform: scaleY(0);
      -webkit-transform-origin: 100% 0%;
      transform-origin: 100% 0%;
      opacity: 1;
    }
    100% {
      -webkit-transform: scaleY(1);
      transform: scaleY(1);
      -webkit-transform-origin: 100% 0%;
      transform-origin: 100% 0%;
      opacity: 1;
    }
  }
  @keyframes scale-in-ver-top {
    0% {
      -webkit-transform: scaleY(0);
      transform: scaleY(0);
      -webkit-transform-origin: 100% 0%;
      transform-origin: 100% 0%;
      opacity: 1;
    }
    100% {
      -webkit-transform: scaleY(1);
      transform: scaleY(1);
      -webkit-transform-origin: 100% 0%;
      transform-origin: 100% 0%;
      opacity: 1;
    }
  }
}

.player-uuid-info {
  padding: 0.45rem 2.5rem 0.45rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 14px 14px 0px 0px;
  position: relative;
  transition: 0.3s all ease;
  cursor: pointer;
  // user-select: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
}

.collapsed-playeruuid {
  border-radius: 14px;
}

.collapse-section {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  position: absolute;
  right: 10px;
  top: 50%;
  // background: #ffffff;
  transform: translate(0%, -50%);
  transition: 0.3s all ease;

  i {
    color: white;
  }
}

.fa-chevron-up {
  transition: 0.3s all ease;
}

.rotate-chevron {
  rotate: 180deg;
  transition: 0.3s all ease;
}

@media (min-width: 768px) {
  .player-card {
    flex-direction: row;
    flex-wrap: wrap;
  }

  .card-section {
    flex: 1 1 calc(50% - 24px);
    min-width: 300px;
  }
}

@media (min-width: 1200px) {
  .card-section {
    flex: 1 1 calc(33.333% - 24px);
  }
}

.card-section {
  /* background: rgba(22, 28, 45, 0.8); */
  background: #ffffff0a;
  overflow: hidden;
  transition: all 0.3s ease;
}

.section-header {
  padding: 0.55rem 1rem;
  display: flex;
  align-items: center;
  user-select: none;
}

.section-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 16px;
}

.section-title {
  margin: 0;
  color: white;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.section-content {
  padding: 0.75rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
}

.info-item {
  display: flex;
  align-items: center;
  padding: 8px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
  transition: all 0.2s ease;
  min-width: 50%;
  border: 1px solid #ffffff17;
  max-width: 100%;
  min-height: 80px;
}

.item-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  font-size: 18px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  flex-shrink: 0;
}

.item-details {
  flex: 1;
  line-height: 1.25;
  overflow: hidden;
}

.item-label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 4px;
  font-weight: 500;
}

.item-value {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: all 0.2s ease;
}

.highlight {
  font-weight: 600;
  color: white;
  font-size: 18px;
  white-space: pre-wrap;
  line-height: 1;
}

/* Icon color classes */
.balance-icon {
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.win-icon {
  background: linear-gradient(135deg, #10b981, #059669);
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

.amount-icon {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);
}

.user-icon {
  background: linear-gradient(135deg, #6366f1, #4f46e5);
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
}

.round-icon {
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
  box-shadow: 0 2px 8px rgba(139, 92, 246, 0.3);
}

.currency-icon {
  background: linear-gradient(135deg, #ec4899, #db2777);
  box-shadow: 0 2px 8px rgba(236, 72, 153, 0.3);
}

.latency-icon {
  background: linear-gradient(135deg, #06b6d4, #0891b2);
  box-shadow: 0 2px 8px rgba(6, 182, 212, 0.3);
}

.cashout-icon {
  background: linear-gradient(135deg, #14b8a6, #0d9488);
  box-shadow: 0 2px 8px rgba(20, 184, 166, 0.3);
}

.date-icon {
  background: linear-gradient(135deg, #a855f7, #9333ea);
  box-shadow: 0 2px 8px rgba(168, 85, 247, 0.3);
}

.token-icon {
  background: linear-gradient(135deg, #559bf7, #9333ea);
  box-shadow: 0 2px 8px rgba(168, 85, 247, 0.3);
}

.connection_id-icon {
  background: linear-gradient(135deg, #0bf5e4, #1f7d66);
  box-shadow: 0 2px 8px rgba(168, 85, 247, 0.3);
}

.autocashout-icon {
  background: linear-gradient(135deg, #c5c20e, #aaa833);
  box-shadow: 0 2px 8px rgba(168, 85, 247, 0.3);
}

.refund-id-icon {
  background: linear-gradient(135deg, #df3112, #9b0707);
  box-shadow: 0 2px 8px rgba(168, 85, 247, 0.3);
}

.default-icon {
  background: linear-gradient(135deg, #475569, #334155);
  box-shadow: 0 2px 8px rgba(71, 85, 105, 0.3);
}

/* Special value styling */
.win-value {
  color: #10b981;
  font-weight: 700;
}

.balance-value {
  color: #51f63b;
  font-weight: 700;
}

.waiting-loader {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 15vh;
}

.type-id-custom {
  zoom: 80%;
  letter-spacing: 0.025em;
}

.uuid-value {
  width: 100%;
  overflow: hidden;
  display: block;
  text-overflow: ellipsis;
  span {
    max-width: 90%;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.history-btn {
  border-radius: 90px;
  background: white;
  padding: 0.25rem 0.45rem;
  margin: 0px 0.45rem;
  font-weight: bold;
  white-space: nowrap;
  color: #5c4242;
  transition: 0.3s all ease;
  border: 1px solid transparent;

  &:hover {
    background-color: #3e454d;
    color: #fff;
    border: 1px solid #fff;
  }
}

.eye-btn {
  transition: 0.3s all ease;
  cursor: pointer;
  &:hover {
    color: greenyellow;
  }
}

.campaign_id-val {
  color: #8dd406;
  font-weight: 700;
  font-size: 12px;
  line-height: 1;
}
</style>
